import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { motion, AnimatePresence } from 'framer-motion';

interface VagueEchoFormProps {
  targetUserId: string;
  targetName: string;
  accentColor: string;
}

// 禁止語句リスト
const FORBIDDEN_WORDS = [
  '死',
  '殺',
  'バカ',
  'ばか',
  '馬鹿',
  'ゴミ',
  'ごみ',
  'クズ',
  'くず',
  '消え',
  '殺す',
  '死ね',
];

/**
 * セッション内で都道府県をキャッシュするキー
 */
const PREFECTURE_CACHE_KEY = 'geo_prefecture';

/**
 * Nominatim Reverse Geocoding で都道府県名を取得する
 */
async function fetchPrefectureFromCoords(
  lat: number,
  lon: number
): Promise<string | null> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&accept-language=ja`,
      { headers: { 'Accept-Language': 'ja' } }
    );
    if (!res.ok) return null;
    const data = await res.json();
    return (data?.address?.state as string) ?? null;
  } catch {
    return null;
  }
}

/**
 * ブラウザの Geolocation API → Nominatim で都道府県を取得する。
 * セッション内でキャッシュし、2回目以降は即座に返す。
 */
async function getPrefecture(): Promise<string | null> {
  const cached = sessionStorage.getItem(PREFECTURE_CACHE_KEY);
  if (cached !== null) {
    return cached === '' ? null : cached;
  }

  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      sessionStorage.setItem(PREFECTURE_CACHE_KEY, '');
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const pref = await fetchPrefectureFromCoords(
          pos.coords.latitude,
          pos.coords.longitude
        );
        sessionStorage.setItem(PREFECTURE_CACHE_KEY, pref ?? '');
        resolve(pref);
      },
      () => {
        sessionStorage.setItem(PREFECTURE_CACHE_KEY, '');
        resolve(null);
      },
      { timeout: 5000, maximumAge: 60000 }
    );
  });
}

/**
 * 1日1回制限のチェック
 */
function canSendToday(targetUserId: string): boolean {
  const today = new Date().toISOString().split('T')[0];
  const key = `vague_echo_${targetUserId}_${today}`;
  return !sessionStorage.getItem(key);
}

/**
 * 送信記録を保存
 */
function markAsSentToday(targetUserId: string): void {
  const today = new Date().toISOString().split('T')[0];
  const key = `vague_echo_${targetUserId}_${today}`;
  sessionStorage.setItem(key, '1');
}

export function VagueEchoForm({ targetUserId, targetName, accentColor }: VagueEchoFormProps) {
  const { user } = useAuth();
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const [canSend, setCanSend] = useState(true);

  useEffect(() => {
    setCanSend(canSendToday(targetUserId));
  }, [targetUserId]);

  const checkForbiddenWords = (text: string): boolean => {
    return FORBIDDEN_WORDS.some((word) => text.includes(word));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!user) {
      setError('ログインが必要です');
      return;
    }

    if (!message.trim()) {
      setError('メッセージを入力してください');
      return;
    }

    if (message.length > 30) {
      setError('メッセージは30文字以内で入力してください');
      return;
    }

    if (!canSend) {
      setError('この場所へは1日1回までしか書き置きできません');
      return;
    }

    if (checkForbiddenWords(message)) {
      setError('もう少し優しい言葉を選んでみませんか？');
      return;
    }

    setIsSubmitting(true);

    try {
      // 都道府県を取得
      const prefecture = await getPrefecture();

      // Supabaseに送信
      const { error: insertError } = await supabase.from('anonymous_echoes').insert({
        target_user_id: targetUserId,
        message: message.trim(),
        prefecture: prefecture ?? null,
      });

      if (insertError) throw insertError;

      // 送信成功
      markAsSentToday(targetUserId);
      setCanSend(false);
      setShowSuccess(true);
      setMessage('');

      // 3秒後に成功メッセージを消す
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Failed to send echo:', err);
      setError('送信に失敗しました。もう一度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative">
      {/* セクションヘッダー */}
      <div className="flex items-center gap-3 mb-6">
        <div
          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
          style={{
            background: '#8b7355',
            boxShadow: '0 0 8px #8b735550',
          }}
        />
        <h3 className="text-gray-600 text-xs tracking-widest uppercase font-light">
          Vague Echo / この場所への書き置き
        </h3>
      </div>

      {/* フォーム */}
      <form onSubmit={handleSubmit} className="relative">
        <div
          className="relative rounded-lg overflow-hidden border transition-all duration-300"
          style={{
            background: 'linear-gradient(135deg, #2a2520, #1a1815)',
            borderColor: canSend ? '#8b735530' : '#4a4a4a30',
          }}
        >
          {/* 古い紙のテクスチャ */}
          <div
            className="absolute inset-0 opacity-[0.03] pointer-events-none"
            style={{
              backgroundImage:
                'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'2\' numOctaves=\'3\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' /%3E%3C/svg%3E")',
            }}
          />

          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={
              canSend
                ? `${targetName}への静かな言葉を残す...`
                : '今日はすでに書き置きを残しました'
            }
            disabled={!canSend || isSubmitting}
            maxLength={30}
            rows={3}
            className="w-full px-5 py-4 bg-transparent text-gray-300 placeholder-gray-700 text-sm leading-relaxed resize-none focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              fontFamily: "'Noto Serif JP', serif",
            }}
          />

          {/* 文字数カウンター */}
          <div className="px-5 pb-3 flex items-center justify-between">
            <span
              className="text-[10px] tracking-wider"
              style={{
                color: message.length > 30 ? '#ff6b6b' : '#8b7355',
              }}
            >
              {message.length} / 30
            </span>

            {canSend && (
              <button
                type="submit"
                disabled={!message.trim() || isSubmitting || !canSend}
                className="px-4 py-2 rounded-md text-xs font-medium tracking-wider transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap cursor-pointer"
                style={{
                  background: 'linear-gradient(135deg, #8b7355, #6b5845)',
                  color: '#f5f5dc',
                  border: '1px solid #8b735550',
                }}
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    <span>送信中...</span>
                  </>
                ) : (
                  <>
                    <i className="ri-quill-pen-line" />
                    <span>静かに残す</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* エラーメッセージ */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mt-3 px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs flex items-center gap-2"
            >
              <i className="ri-error-warning-line" />
              <span>{error}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* 成功メッセージ（消えていくアニメーション） */}
        <AnimatePresence>
          {showSuccess && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95, y: -20 }}
              transition={{ duration: 0.5 }}
              className="mt-3 px-4 py-3 rounded-lg border text-center"
              style={{
                background: 'linear-gradient(135deg, #8b735520, #6b584510)',
                borderColor: '#8b735530',
                color: '#8b7355',
              }}
            >
              <div className="flex items-center justify-center gap-2 mb-1">
                <i className="ri-check-line" />
                <span className="text-sm font-medium">あなたの言葉が届きました</span>
              </div>
              <p className="text-[10px] text-gray-600">
                この気配は、静かに{targetName}の聖域に残されます
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </form>

      {/* 説明文 */}
      <p className="mt-4 text-gray-700 text-[11px] leading-relaxed text-center">
        ここに残された言葉は、完全に匿名で届けられます。
        <br />
        あなたの名前やアイコンは一切表示されず、都道府県のみが小さく添えられます。
      </p>
    </div>
  );
}